import { useState, useContext } from 'react';
import {
  ShoppingCartIcon,
  ReceiptIcon,
  TruckIcon,
  TagIcon,
} from '@phosphor-icons/react';
import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAgentState } from '@/context/agent-state.context';
import { AuthContext } from '@/context/auth.context';
import { ChatMessageList } from '@/components/common/chat/ChatMessageList';
import { ChatInput } from '@/components/common/chat/ChatInput';
import { TaskList } from './components/TaskList';
import { useHomeTasks } from './hooks/useHomeTasks';

interface RunningModule {
  module: string;
  items: { label: string; count: number }[];
}

const STATIC_DATA_CARDS = [
  { label: 'Total Tasks', value: 19 },
  { label: 'High Priority', value: 5 },
  { label: 'Running', value: 30 },
  { label: 'Completed Today', value: 5 },
];

const STATIC_RUNNING: RunningModule[] = [
  {
    module: 'Procurement',
    items: [
      { label: 'workflows', count: 5 },
      { label: 'calls', count: 3 },
    ],
  },
  { module: 'AP', items: [{ label: 'workflows', count: 10 }] },
  {
    module: 'OMS',
    items: [
      { label: 'workflows', count: 100 },
      { label: 'inbound', count: 41 },
      { label: 'calls', count: 4 },
    ],
  },
  {
    module: 'Rebates',
    items: [
      { label: 'agreements', count: 7 },
      { label: 'forecasts', count: 7 },
      { label: 'collections', count: 4 },
    ],
  },
];

const MODULE_FILTERS = [
  'All',
  'Procurement',
  'Accounts Payable',
  'OMS',
  'Rebates',
  'Ship & Debit',
];

const MODULE_ICONS: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  Procurement: ShoppingCartIcon,
  AP: ReceiptIcon,
  OMS: TruckIcon,
  Rebates: TagIcon,
};

function getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function generateWelcomeMessage(
  firstName: string,
  totalCount: number,
  highPriorityCount: number,
): string {
  const timeOfDay = getTimeOfDay();
  const runningCount = STATIC_RUNNING.reduce(
    (acc, mod) => acc + mod.items.reduce((sum, item) => sum + item.count, 0),
    0,
  );

  if (totalCount === 0) {
    return `Good ${timeOfDay}, ${firstName}.

Welcome back to your ERP Home dashboard. You're all caught up—no pending tasks at the moment! There are **${runningCount} processes running** in the system.

What would you like to work on today?`;
  }

  return `Good ${timeOfDay}, ${firstName}.

Welcome back to your ERP Home dashboard. You currently have **${totalCount} pending tasks**${highPriorityCount > 0 ? `, including **${highPriorityCount} high-priority** items` : ''}, and there are **${runningCount} processes running** in the system.

What would you like to tackle first—review the high-priority tasks, check running processes, or navigate to another module?`;
}

export function HomePage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [inputValue, setInputValue] = useState('');

  const { currentUser } = useContext(AuthContext);
  const {
    isLoading,
    messages,
    sendMessage,
    streamingText,
    activeToolCalls,
    error,
  } = useAgentState();
  const { totalCount, highPriorityCount } = useHomeTasks();

  const firstName = currentUser?.name?.split(' ')[0] ?? 'there';
  const welcomeContent = generateWelcomeMessage(
    firstName,
    totalCount,
    highPriorityCount,
  );

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;
    sendMessage(trimmed, 'home', '/home');
    setInputValue('');
  };

  return (
    <Page title="Home">
      <div
        className="flex flex-col gap-3"
        style={{ height: 'calc(100vh - 140px)' }}
      >
        <div className="grid grid-cols-4 gap-3">
          {STATIC_DATA_CARDS.map((card) => (
            <Card key={card.label}>
              <CardHeader className="pb-1">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{card.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex gap-2 flex-wrap shrink-0">
          {MODULE_FILTERS.map((filter) => (
            <Button
              key={filter}
              variant={activeFilter === filter ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => setActiveFilter(filter)}
            >
              {filter}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-12 gap-3 flex-1 min-h-0">
          <Card className="col-span-3 flex flex-col min-h-0">
            <CardHeader className="border-b pb-2 shrink-0">
              <CardTitle className="text-base">Tasks</CardTitle>
            </CardHeader>
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-3">
                <TaskList />
              </div>
            </ScrollArea>
          </Card>

          <Card className="col-span-6 flex flex-col min-h-0">
            <CardHeader className="border-b pb-2 shrink-0">
              <CardTitle className="text-base">Assistant</CardTitle>
            </CardHeader>
            <ChatMessageList
              messages={messages}
              streamingText={streamingText}
              isLoading={isLoading}
              error={error}
              activeToolCalls={activeToolCalls}
              welcomeContent={welcomeContent}
              size="sm"
            />
            <div className="p-3 border-t">
              <ChatInput
                value={inputValue}
                onChange={setInputValue}
                onSend={handleSend}
                isLoading={isLoading}
                size="sm"
                enableVoiceInput={true}
              />
            </div>
          </Card>

          <Card className="col-span-3 flex flex-col min-h-0">
            <CardHeader className="border-b pb-2 shrink-0">
              <CardTitle className="text-base">Running</CardTitle>
            </CardHeader>
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-3 space-y-3">
                {STATIC_RUNNING.map((mod) => {
                  const Icon = MODULE_ICONS[mod.module];
                  return (
                    <div key={mod.module}>
                      <div className="flex items-center gap-2 font-medium text-sm mb-1">
                        {Icon && (
                          <Icon className="h-4 w-4 text-muted-foreground" />
                        )}
                        {mod.module}
                      </div>
                      <div className="pl-6 space-y-0.5">
                        {mod.items.map((item) => (
                          <div
                            key={item.label}
                            className="text-xs text-muted-foreground"
                          >
                            {item.count} {item.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </Card>
        </div>
      </div>
    </Page>
  );
}
